import React from 'react';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';
import {
  STEP_LINK_QUERY_PARAM_NAME,
  useSurveyContext,
} from 'Views/Onboarding/Pages/Survey/SurveyContext';
import { addQueryWithReplace } from 'Helpers/Query';
import useEventTrackingLogEvent, { categories, eventTypes, eventSubTypes } from 'EventTracking/useEventTrackingLogEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';

const SurveyStepContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const SUBMIT_STARTED = 'submit-started';
export const SUBMIT_SUCCEEDED = 'submit-succeeded';
export const SUBMIT_FAILED = 'submit-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    isSubmitting: false,
    submittingError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        onboardingStepCollection: action.onboardingStepCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case SUBMIT_STARTED: {
      return {
        ...state,
        isSubmitting: true,
        submittingError: null,
      };
    }
    case SUBMIT_SUCCEEDED: {
      return {
        ...state,
        isSubmitting: false,
        submittingError: null,
      };
    }
    case SUBMIT_FAILED: {
      return {
        ...state,
        isSubmitting: false,
        submittingError: action.submittingError,
      };
    }
    default: {
      throw new Error(`Unhandled SurveyContext action type: ${action.type}`);
    }
  }
};

const SurveyStepProvider = ({
  children,
}) => {
  const {
    onboardingStepCollection,
    setOnboardingStepCollection,
    loadPreviousOnboardingStepCollection,
    loadNextOnboardingStepCollection,
  } = useSurveyContext();
  const { displayErrorMessage } = useSnackbarContext();

  const { logStandardizedEvent } = useEventTrackingLogEvent({
    nodeName: 'Survey Step',
    isPageView: true,
  });
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const handlePrevious = React.useCallback(() => {
    logStandardizedEvent(categories.digitalOnboarding, eventTypes.clickButton, eventSubTypes.start, { eventProperties: { description: 'Go to Previous Step' } });
    return loadPreviousOnboardingStepCollection();
  }, [loadPreviousOnboardingStepCollection]);

  const goToNextStep = React.useCallback(() => {
    return loadNextOnboardingStepCollection();
  }, [loadPreviousOnboardingStepCollection]);

  const handleSubmit = React.useCallback(async (onSubmit) => {
    try {
      dispatch({ type: SUBMIT_STARTED });

      const [currStepResource] = onboardingStepCollection;
      logStandardizedEvent(categories.digitalOnboarding, eventTypes.completeStep, eventSubTypes.start, {
        eventProperties: {
          stepName: `${currStepResource.attr('moduleName')} - ${currStepResource.type()}`,
        },
      });

      const coll = await onSubmit();

      if (coll) {
        const [resource] = coll;
        addQueryWithReplace({ [STEP_LINK_QUERY_PARAM_NAME]: resource.link('self').url() });
        setOnboardingStepCollection(coll);
      }

      dispatch({ type: SUBMIT_SUCCEEDED });
    } catch (err) {
      displayErrorMessage(err, 'An unexpected error occurred while submitting the step');
      dispatch({ type: SUBMIT_FAILED, submittingError: err });
    }
  }, [onboardingStepCollection]);

  const memoizedValue = React.useMemo(() => {
    const hasNextStep = onboardingStepCollection.meta().canPost();
    const hasPreviousStep = onboardingStepCollection?.resources().head().link('prev') !== null;

    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      isSubmitting: state.isSubmitting,
      submittingError: state.submittingError,
      onClickPrevious: (hasPreviousStep) ? handlePrevious : null,
      hasNextStep,
      onClickNext: handleSubmit,
      setOnboardingStepCollection,
      goToNextStep,
    };
  }, [
    state,
    onboardingStepCollection,
    handlePrevious,
    handleSubmit,
    goToNextStep,
  ]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      // Do initialization here

      dispatch({
        type: INITIALIZE_SUCCEEDED,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_STARTED, initializationError: err });
    }
  });

  return (
    <SurveyStepContext.Provider value={memoizedValue}>
      {children}
    </SurveyStepContext.Provider>
  );
};

// Use Survey State Context
const useSurveyStepContext = () => {
  const context = React.useContext(SurveyStepContext);
  if (context === undefined) {
    throw new Error('useSurveyStepContext must be used within a SurveyStepProvider');
  }
  return context;
};

SurveyStepProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  SurveyStepProvider,
  useSurveyStepContext,
};
